<script>
  import { createEventDispatcher } from "svelte";

  const dispatch = createEventDispatcher();

  export let isShow = false;
  export let src;
  export let imagesCount;
  export let currentImageIndex;

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;

  let scale = 1;
  let x = 0;
  let y = 0;
  let isDragging = false;
  let startX = 0;
  let startY = 0;
  let initialPinchDistance = null;
  let initialImageWidth;
  let initialImageHeight;
  let imageElement;
  let imageContainer;

  const handleImageLoad = (event) => {
    imageElement = event.target;
    // Пропускаем, если изображение еще не загружено
    if (!imageElement.complete) {
      return;
    }
    initialImageWidth = imageElement.offsetWidth;
    initialImageHeight = imageElement.offsetHeight;
    resetPositionAndZoom();
  };

  const resetPositionAndZoom = () => {
    if (!imageElement || !imageContainer) {
      return;
    }
    scale = 1;
    x = 0;
    y = 0;
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    const newScale = scale + delta;
    setZoom(newScale, event.clientX, event.clientY);
  };

  const setZoom = (newScale, cx, cy) => {
    if (!imageElement || !imageContainer) {
      return;
    }

    const containerWidth = imageContainer.offsetWidth;
    const containerHeight = imageContainer.offsetHeight;
    const limitedScale = Math.max(MIN_SCALE, Math.min(newScale, MAX_SCALE));

    if (cx !== undefined && cy !== undefined) {
      const rect = imageElement.getBoundingClientRect();
      const offsetX = cx - rect.left;
      const offsetY = cy - rect.top;

      // Ограничение смещения
      const scaledWidth = initialImageWidth * limitedScale;
      const scaledHeight = initialImageHeight * limitedScale;

      const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
      const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

      x -= offsetX / scale - offsetX / limitedScale;
      y -= offsetY / scale - offsetY / limitedScale;

      x = Math.max(-maxX, Math.min(x, maxX));
      y = Math.max(-maxY, Math.min(y, maxY));
    }

    scale = limitedScale;
    limitImagePosition();
  };

  const handleMouseDown = (event) => {
    isDragging = true;
    startX = event.clientX - x;
    startY = event.clientY - y;
    if (imageElement) {
      imageElement.style.cursor = "grabbing";
    }
  };

  const handleMouseMove = (event) => {
    if (!isDragging) {
      return;
    }
    x = event.clientX - startX;
    y = event.clientY - startY;
    limitImagePosition();
  };

  const handleMouseUp = () => {
    isDragging = false;
    if (imageElement) {
      imageElement.style.cursor = "grab";
    }
  };

  const handleMouseLeave = () => {
    isDragging = false;
    if (imageElement) {
      imageElement.style.cursor = "grab";
    }
  };

  const handleTouchStart = (event) => {
    if (event.touches.length === 1) {
      isDragging = true;
      startX = event.touches[0].clientX - x;
      startY = event.touches[0].clientY - y;
    } else if (event.touches.length === 2) {
      initialPinchDistance = getPinchDistance(event.touches);
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 1 && isDragging) {
      x = event.touches[0].clientX - startX;
      y = event.touches[0].clientY - startY;
      limitImagePosition();
    } else if (event.touches.length === 2 && initialPinchDistance) {
      const currentPinchDistance = getPinchDistance(event.touches);
      const scaleChange = currentPinchDistance / initialPinchDistance;
      setZoom(scale * scaleChange);
      initialPinchDistance = currentPinchDistance;
    }
  };

  const handleTouchEnd = (event) => {
    if (event.touches.length < 2) {
      isDragging = false;
      initialPinchDistance = null;
    }
  };

  const getPinchDistance = (touches) => {
    return Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    );
  };

  const limitImagePosition = () => {
    if (!imageElement || !imageContainer) {
      return;
    }

    const containerWidth = imageContainer.offsetWidth;
    const containerHeight = imageContainer.offsetHeight;
    const scaledWidth = initialImageWidth * scale;
    const scaledHeight = initialImageHeight * scale;

    const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

    x = Math.max(-maxX, Math.min(x, maxX));
    y = Math.max(-maxY, Math.min(y, maxY));
  };

  const closePopup = () => {
    dispatch("close");
  };
</script>

{#if isShow}
  <div class="popup" on:click={closePopup}>
    <i class="icon-cross close" />

    <div class="content-wrapper">
      <div
        class="image-container"
        bind:this={imageContainer}
        on:wheel={handleWheel}
        on:mousedown={handleMouseDown}
        on:mousemove={handleMouseMove}
        on:mouseup={handleMouseUp}
        on:mouseleave={handleMouseLeave}
        on:touchstart={handleTouchStart}
        on:touchmove={handleTouchMove}
        on:touchend={handleTouchEnd}
      >
        <img
          bind:this={imageElement}
          {src}
          alt="Image"
          style="transform: translate({x}px, {y}px) scale({scale});"
          draggable="false"
          on:load={handleImageLoad}
          on:click|stopPropagation
        />
      </div>
      {#if imagesCount > 1}
        <div class="navigation-wrapper" on:click|stopPropagation>
          <button
            class="navigation-wrapper__btn"
            disabled={currentImageIndex <= 0}
            on:click|stopPropagation={() =>
              dispatch("change-image", { type: "prev" })}
          >
            <i
              class="icon-arrow-left"
              class:disabled={currentImageIndex <= 0}
            />
          </button>

          <button
            class="navigation-wrapper__btn"
            disabled={currentImageIndex === imagesCount - 1}
            on:click|stopPropagation={() =>
              dispatch("change-image", { type: "next" })}
          >
            <i
              class="icon-arrow-right"
              class:disabled={currentImageIndex === imagesCount - 1}
            />
          </button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
  .popup {
    position: fixed;
    z-index: 1111111111111;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(34, 37, 44, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .content-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .image-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    cursor: grab;
    touch-action: none;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    user-select: none;
    -webkit-user-drag: none;
    touch-action: none;
    will-change: transform; // Оптимизация производительности
    border-radius: 8px;
  }

  .navigation-wrapper {
    width: inherit;
    height: 72px;
    position: fixed;
    left: 0;
    bottom: 0;
    background-color: rgba(34, 37, 44, 0.4);

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;

    &__btn {
      padding: 0;
      height: 100%;
      background-color: transparent;
      border: none;
      cursor: pointer;
      // Убираем выделение при нажатии в моб-ых версиях
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }
  }

  .icon-arrow-left,
  .icon-arrow-right {
    color: white;
    font-size: 26px;
    z-index: 1;

    &::before {
      width: auto;
    }

    &.disabled {
      color: rgba(90, 95, 109, 0.6);
      pointer-events: none;
    }
  }

  .close {
    padding: 4px;
    position: absolute;
    top: 40px;
    right: 40px;
    color: white;
    background-color: rgba(34, 37, 44, 0.4);
    border-radius: 5px;
    font-size: 20px;
    cursor: pointer;
    z-index: 1;
  }

  @media (max-width: 576px) {
    .content-wrapper {
      width: 95%;
      height: 80%;
    }

    .image-container {
      border-radius: 8px;
    }

    .close {
      top: 20px;
      right: 20px;
      font-size: 12px;
    }

    .navigation-wrapper {
      margin: 0 10px;
      height: 40px;
      bottom: 20px;
      border-radius: 8px;
    }

    .icon-arrow-left,
    .icon-arrow-right {
      font-size: 16px;
    }
  }
</style>
